import {
  AfterViewChecked,
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { animate, style, transition, trigger } from '@angular/animations';
import { Router } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, switchMap } from 'rxjs/operators';
import { AuctionLotDto } from 'src/app/models/auction-lot';
import { AuctionLotsService } from 'src/app/services/auction-lots-service';
import { Registration } from 'src/app/models/registration';
import { platePath } from 'src/app/utils/canonical-url';
import { ImageService } from 'src/app/services/image-service';
import { PlateViewerConfig } from 'src/app/models/plateViewerConfig';
import {
  PresentationSource,
  resolvePresentationAsset,
} from 'src/app/utils/presentation-asset';

/**
 * Search 12 results BODY for dev2 — the listing counterpart to Plate Detail V2.
 *
 * Presentational only: it receives the live results the parent
 * SearchResultsPageComponent has already fetched (Mode 2 / legacy search,
 * untouched) and renders them in the Search 12 layout, reusing <pla-plate> for
 * every plate. No new search / ranking. Sold / market have no live feed → clean
 * empty states. A dev2-only `?fixture=search12` populates K / JAMES with
 * realistic visual-review data (never affects production search or data).
 */

interface PerfectCard {
  reg: Registration;
  label: string;
  img: string | null; // detail-page-style zoom render (lazy); null = fallback stage
  cfg: PlateViewerConfig | null; // curated premium vehicle for this card (distinct)
  carName: string | null;
  // Presentation-continuity identity — carried into Plate Detail so the hero
  // image matches the car shown here. See utils/presentation-asset.ts.
  assetId: string;
  source: PresentationSource;
}

type TabKey = 'close' | 'similar' | 'new' | 'current' | 'prefix' | 'market';

interface QChip {
  label: string;
  link: string;
}
interface TabDef {
  key: TabKey;
  label: string;
  count: number | null;
}

// A wildcard pattern group from the backend's extended-search (e.g. the
// 'HU??GH?' / '????HUG' groups). Each group is rendered as its own paginated
// block inside the Current / Prefix sections — never flattened into one list.
interface PatternGroup {
  label: string;
  regs: Registration[];
  // True group total from the backend (rows are capped server-side ~100/group);
  // when absent, regs.length is used.
  count?: number;
}
// A ready-to-render pattern block: heading, a stable pagination key, the rows we
// actually have (loaded, pre-capped by the backend), `loaded` = rows.length for
// the "See more" control, and `count` = the true group total for the badge.
interface GroupView {
  label: string;
  key: string;
  rows: Registration[];
  loaded: number;
  count: number;
}

// realistic visual-review fixtures (dev2 ?fixture=search12 only)
function fx(
  formatted: string,
  pricePounds: number,
  style: string,
  sellers?: number
): Registration {
  return {
    registration: formatted.replace(/\s+/g, ''),
    formattedRegistration: formatted,
    price: pricePounds * 100,
    style,
    sellers,
  } as any as Registration;
}
interface Fixture {
  perfect: Registration[];
  rows: Registration[];
  found: number; // total registrations found (headline count)
  close: number; // close-match count
  newCount: number;
  pricedFrom: number; // pounds (Priced From stat)
}
const FIXTURES: { [term: string]: Fixture } = {
  K: {
    // '8 K' left unpriced (0) to exercise the available-first ordering + the
    // secondary "Not currently available" perfect card.
    perfect: [fx('1 K', 1250000, 'dateless'), fx('K 1', 880000, 'dateless'), fx('8 K', 0, 'dateless')],
    rows: [
      fx('K 11', 18500, 'dateless', 4),
      fx('11 K', 21000, 'dateless', 3),
      fx('K 21', 24950, 'dateless', 2),
      fx('K 7', 36000, 'dateless', 5),
      fx('7 K', 38500, 'dateless', 3),
      fx('K 24', 41000, 'dateless', 2),
      fx('K 9', 0, 'dateless'),        // registry-only, not currently listed
      fx('24 K', 44950, 'dateless', 4),
      fx('50 K', 52000, 'dateless', 6),
      fx('K 100', 58500, 'dateless', 2),
      fx('2 K', 0, 'dateless'),        // registry-only, not currently listed
      fx('72 K', 100114, 'dateless', 6),
      fx('5 K', 595080, 'dateless', 2),
      fx('4 K', 625030, 'dateless', 2),
    ],
    found: 312,
    close: 100,
    newCount: 6,
    pricedFrom: 18500,
  },
  JAMES: {
    perfect: [fx('J4 MES', 335160, 'prefix'), fx('JAM 3S', 8995, 'dateless'), fx('JAM 35', 0, 'dateless')],
    rows: [
      fx('JAM 35', 3950, 'dateless', 3),
      fx('JM 11', 6500, 'dateless', 2),
      fx('JME 5', 7250, 'dateless', 4),
      fx('JA51 MES', 8995, 'current', 5),
      fx('J4 MES', 0, 'prefix'),       // registry-only, not currently listed
      fx('JA13 MES', 9995, 'current', 3),
      fx('J7 MES', 11500, 'prefix', 2),
      fx('JAM 3S', 12995, 'dateless', 3),
      fx('JA55 MES', 13500, 'current', 4),
      fx('J9 MES', 14950, 'prefix', 2),
      fx('J1 MES', 0, 'prefix'),       // registry-only, not currently listed
      fx('JAM 1E', 16500, 'dateless', 3),
    ],
    found: 186,
    close: 67,
    newCount: 4,
    pricedFrom: 399,
  },
};

@Component({
  selector: 'pla-search-body-v2',
  templateUrl: './search-body-v2.component.html',
  styleUrls: ['./search-body-v2.component.scss'],
  animations: [
    // Subtle fade/slide for rows that are genuinely new (a later search leg merging in).
    // trackBy keeps existing rows' DOM nodes, so only new rows animate; [@.disabled] on the
    // body root suppresses the whole first paint so the initial list does not flash in.
    trigger('rowAnim', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-6px)' }),
        animate('180ms ease-out', style({ opacity: 1, transform: 'none' })),
      ]),
    ]),
  ],
})
export class SearchBodyV2Component
  implements OnInit, OnChanges, AfterViewInit, AfterViewChecked, OnDestroy
{
  /** The search term (parent's searchCriteria). */
  @Input() term = '';
  /** Live merged results (parent's combined_main_results.registrations). */
  @Input() registrations: Registration[] = [];
  /** Live perfect/exact matches (parent's perfectResults.registrations). */
  @Input() perfect: Registration[] = [];
  /** True while the primary search is still running. */
  @Input() loading = false;
  /** Synonym / "Similar" results (parent's synonymResults, flattened). */
  @Input() similar: Registration[] = [];
  /** Current-style results (parent's currentResults). */
  @Input() current: Registration[] = [];
  /** Prefix-style results (parent's prefixResults). */
  @Input() prefix: Registration[] = [];
  /**
   * Current-style wildcard pattern groups (parent's currentResults, one entry
   * per wildcard version e.g. 'HU??GH?'). When present these are rendered as
   * separate paginated blocks instead of one flat list — this is what replaces
   * the old ~16k flat "Current" render.
   */
  @Input() currentGroups: PatternGroup[] = [];
  /** Prefix-style wildcard pattern groups (parent's prefixResults). */
  @Input() prefixGroups: PatternGroup[] = [];

  /** Reflects the parent's real saved-search state (dev2 account service). */
  @Input() searchSaved = false;
  /** Raised when the user asks to save/track this search; parent persists it. */
  @Output() saveSearch = new EventEmitter<void>();

  // The tab buttons are now scroll anchors, not content switches. `activeTab`
  // only drives which tab reads "active" as the page scrolls (scroll-spy).
  activeTab: TabKey = 'close';
  perfectCards: PerfectCard[] = [];
  fixtureMode = false;

  // ---- per-section pagination ("See more, showing X of N") ----
  // Every list / pattern block starts collapsed to PAGE_INIT rows and grows by
  // PAGE_STEP on each "See more", so no section ever renders thousands of rows.
  private readonly PAGE_STEP = 20;
  private shownCaps: { [key: string]: number } = {};
  // Initial rows per section: Close Matches shows 20, every other section 10.
  private initFor(key: string): number {
    return key === 'close' ? 20 : 10;
  }
  private cap(key: string): number {
    return this.shownCaps[key] || this.initFor(key);
  }
  // Rows currently visible for a block (capped). `key` namespaces each block so
  // "See more" on one group never affects another.
  pageRows(key: string, list: Registration[]): Registration[] {
    return (list || []).slice(0, this.cap(key));
  }
  shownCount(key: string, total: number): number {
    return Math.min(this.cap(key), total);
  }
  hasMore(key: string, total: number): boolean {
    return this.cap(key) < total;
  }
  showMore(key: string, total: number): void {
    this.shownCaps[key] = Math.min(this.cap(key) + this.PAGE_STEP, total);
  }
  private resetPagination(): void {
    this.shownCaps = {};
  }
  // optimistic local flag so the button confirms immediately on click
  private savedLocal = false;
  get saved(): boolean {
    return this.savedLocal || this.searchSaved;
  }
  onSaveSearch(): void {
    this.savedLocal = true;
    this.saveSearch.emit();
  }

  // Drives the existing pla-new-search-input (which self-navigates to
  // /search-results/:term) so the searched plate is a real, editable search.
  searchTrigger: EventEmitter<void> = new EventEmitter<void>();

  typeFilter: '' | 'current' | 'prefix' | 'suffix' | 'dateless' = '';
  saleType: '' | 'new' | 'resale' = '';
  priceBand: '' | 'u500' | 'mid' | 'high' | 'top' = '';
  // 'relevant' = keep the backend score order (default, matches prod "Best Match");
  // 'low'/'high' = sort by price ascending/descending.
  sortMode: 'relevant' | 'low' | 'high' = 'relevant';
  filterOpen = false;
  sortOpen = false;

  private readonly perfectLabels = [
    'Perfect Match',
    'Closest Alternative',
    'Trophy Plate',
  ];

  // Latest live New Issue series (drives the dynamic "{NN} Reg" chip + its link).
  // 76 is the current series (26 still selling, 76 launched) per main-nav-chips.
  readonly currentSeries = '76';

  // Quick-pick chips (V13 §4) — 8 chips, each links to its category page.
  // "{NN} Reg" + Build-Your-Own wired live.
  qchips: QChip[] = [
    { label: '1x1', link: '/dateless-search/1' },
    { label: 'No.1', link: '/number-1' },
    { label: 'Names', link: '/names' },
    { label: 'Initials', link: '/initials-search' },
    { label: this.currentSeries + ' Reg', link: '/plates/' + this.currentSeries + '-reg' },
    { label: 'Dateless', link: '/dateless-search' },
    { label: 'Under £200', link: '/under-200' },
    { label: 'Build-Your-Own', link: '/byo-search' },
  ];

  // Recent searches — the user's actual recent search terms (localStorage-backed).
  recentSearches: string[] = [];
  private readonly RECENT_KEY = 'pla-recent-searches';
  // Only a few compact chips are shown beside the heading (rest live in history).
  get recentChips(): string[] {
    return (this.recentSearches || []).slice(0, 3);
  }

  constructor(
    private router: Router,
    private imageService: ImageService,
    private auctionLots: AuctionLotsService,
    private host: ElementRef
  ) {}

  // ===== Progressive-merge polish (stable identity + subtle transitions + scroll) =====
  // Dev2 already paints the first Mode 2 partition immediately, merges progressively, keeps
  // pagination across merges and dedupes. These add the remaining production-parity niceties
  // WITHOUT changing Dev2's ranking, dedup or coverage: rows keep their identity across a merge
  // (trackBy), new rows fade in, and the viewport is held across a ranked re-merge.
  public animDisabled = true;           // suppress row :enter until the first real paint
  private _anchorReg: string | null = null;
  private _anchorTop = 0;
  private _restoreAnchor = false;

  // Stable row identity: keyed on the registration string so a merge moves rows instead of
  // destroying and recreating them (preserves focus/hover and lets the browser hold scroll).
  public trackByReg(index: number, r: Registration): any {
    return r ? r.registration : index;
  }
  // Stable group identity, so a rebuilt group list keeps each group's DOM (and its rows).
  public trackByGroup(index: number, g: GroupView): any {
    return g ? g.key : index;
  }

  // Pin the first Close-Matches row at/below the viewport top before a merge changes the list.
  private captureAnchor(): void {
    this._restoreAnchor = false;
    if (this.animDisabled) return;
    try {
      const root: HTMLElement = this.host.nativeElement;
      const sec = root.querySelector('#sb-sec-close') || root;
      const rows = sec.querySelectorAll('[data-reg]');
      for (let i = 0; i < rows.length; i++) {
        const el = rows[i] as HTMLElement;
        const top = el.getBoundingClientRect().top;
        if (top >= 0) {
          this._anchorReg = el.getAttribute('data-reg');
          this._anchorTop = top;
          this._restoreAnchor = true;
          return;
        }
      }
    } catch (_) { /* best-effort; native scroll anchoring remains the fallback */ }
  }

  ngAfterViewChecked(): void {
    // Enable row-enter animation only after the first real paint (rows present), so the initial
    // list does not flash in row-by-row.
    if (this.animDisabled) {
      try {
        if ((this.host.nativeElement as HTMLElement).querySelector('#sb-sec-close [data-reg]')) {
          setTimeout(() => { this.animDisabled = false; }, 60);
        }
      } catch (_) {}
      return;
    }
    if (!this._restoreAnchor || this._anchorReg == null) return;
    this._restoreAnchor = false;
    try {
      const root: HTMLElement = this.host.nativeElement;
      const sec = root.querySelector('#sb-sec-close') || root;
      const rows = sec.querySelectorAll('[data-reg]');
      for (let i = 0; i < rows.length; i++) {
        const el = rows[i] as HTMLElement;
        if (el.getAttribute('data-reg') === this._anchorReg) {
          const delta = el.getBoundingClientRect().top - this._anchorTop;
          if (Math.abs(delta) > 1) window.scrollBy(0, delta);
          return;
        }
      }
    } catch (_) { /* anchor scrolled out of view; leave position as-is */ }
  }

  // ================= Live auctions (DEV-only, fail-silent) =================
  // Every registration in the search's FULL result set (all buckets, not just
  // the rendered rows) is checked with one POST /lots/by-registrations
  // (chunked at 5,000). Plates live at auction are pinned to the top of the tab
  // they belong to (coming-soon after live) with an "At auction" status; the
  // perfect cards keep their placement but get the auction treatment.
  private auctionInfo = new Map<string, AuctionInfo>();
  private auctionKeys = '';
  private auctionReq$ = new Subject<string[]>();
  private auctionSub?: Subscription;

  private regKey(r: Registration | null | undefined): string {
    return ((r && r.registration) || '').replace(/\s+/g, '').toUpperCase();
  }

  private requestAuctions(): void {
    const keys = new Set<string>();
    [this.effRegs, this.effPerfect, this.similarList, this.currentList, this.prefixList].forEach((list) =>
      (list || []).forEach((r) => {
        const k = this.regKey(r);
        if (k) keys.add(k);
      })
    );
    const sorted = Array.from(keys).sort();
    const sig = sorted.join(',');
    if (!sorted.length || sig === this.auctionKeys) return;
    this.auctionKeys = sig;
    this.auctionReq$.next(sorted);
  }

  private setAuctions(lots: Map<string, AuctionLotDto>): void {
    const info = new Map<string, AuctionInfo>();
    const now = Date.now();
    lots.forEach((lot, key) => {
      const i = toAuctionInfo(lot, now);
      if (i) info.set(key, i);
    });
    this.auctionInfo = info;
    // Auctions pin to the top of their section — re-derive the cached views.
    this.recompute();
  }

  /** Live / coming-soon auction for a registration (null = none, or closed). */
  auctionOf(r: Registration | null | undefined): AuctionInfo | null {
    if (!this.auctionInfo.size) return null;
    const i = this.auctionInfo.get(this.regKey(r));
    if (!i) return null;
    if (i.closesAt && Date.now() >= i.closesAt) return null;
    return i;
  }

  /** Distinct registrations in the full result set that are at auction. */
  get auctionCount(): number {
    let n = 0;
    this.auctionInfo.forEach((i) => {
      if (!i.closesAt || Date.now() < i.closesAt) n++;
    });
    return n;
  }

  /** Live lots first (ending soonest), then coming soon (opening soonest), then the rest. */
  private pinAuctions(list: Registration[]): Registration[] {
    if (!this.auctionInfo.size) return list;
    const live: Registration[] = [];
    const soon: Registration[] = [];
    const rest: Registration[] = [];
    list.forEach((r) => {
      const a = this.auctionOf(r);
      if (!a) rest.push(r);
      else (a.kind === 'live' ? live : soon).push(r);
    });
    if (!live.length && !soon.length) return list;
    live.sort((a, b) => (this.auctionOf(a)!.closesAt || 0) - (this.auctionOf(b)!.closesAt || 0));
    soon.sort((a, b) => (this.auctionOf(a)!.opensAt || 0) - (this.auctionOf(b)!.opensAt || 0));
    return [...live, ...soon, ...rest];
  }

  ngOnDestroy(): void {
    this.auctionSub?.unsubscribe();
  }

  ngOnInit(): void {
    this.auctionSub = this.auctionReq$
      .pipe(
        debounceTime(400),
        switchMap((keys) => this.auctionLots.getLotsByRegistrations(keys))
      )
      .subscribe((m) => this.setAuctions(m));
    this.fixtureMode =
      typeof window !== 'undefined' &&
      /[?&]fixture=search12/.test(window.location.search);
    this.loadRecent();
    this.recordRecent(); // remember the term just searched
    this.buildPerfect();
    this.recompute();
    this.requestAuctions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // A progressive merge (new registrations) changes the Close-Matches list: pin the scroll
    // position BEFORE the view updates, unless the term itself changed (a fresh search starts
    // at the top). Reads the pre-update DOM; restored in ngAfterViewChecked.
    if (changes['registrations'] && !changes['term']) this.captureAnchor();
    if (changes['perfect'] || changes['term']) this.buildPerfect();
    if (
      changes['registrations'] || changes['perfect'] || changes['similar'] ||
      changes['current'] || changes['prefix'] ||
      changes['currentGroups'] || changes['prefixGroups'] || changes['term']
    ) {
      this.recompute();
      this.requestAuctions();
    }
    // A new search term starts every section fresh at PAGE_INIT.
    if (changes['term'] && !changes['term'].firstChange) {
      this.resetPagination();
      this.recordRecent();
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.checkQpillsOverflow());
  }

  // ---- collection pills: side-scroll instead of wrapping to a 2nd row ----
  @ViewChild('qpillScroller') qpillScroller?: ElementRef<HTMLElement>;
  qpillsOverflow = false;

  @HostListener('window:resize')
  onResize(): void {
    this.checkQpillsOverflow();
  }
  onQpillsScroll(): void {
    this.checkQpillsOverflow();
  }
  private checkQpillsOverflow(): void {
    const el = this.qpillScroller?.nativeElement;
    if (!el) return;
    // hide the arrow once scrolled to (near) the end
    this.qpillsOverflow = el.scrollWidth - el.clientWidth - el.scrollLeft > 4;
  }
  scrollQpills(): void {
    const el = this.qpillScroller?.nativeElement;
    if (!el) return;
    el.scrollBy({ left: Math.round(el.clientWidth * 0.7), behavior: 'smooth' });
  }

  // ---- recent searches (persisted) ----
  private loadRecent(): void {
    try {
      const raw = localStorage.getItem(this.RECENT_KEY);
      this.recentSearches = raw ? JSON.parse(raw) : [];
    } catch {
      this.recentSearches = [];
    }
  }
  private recordRecent(): void {
    const t = (this.term || '').trim();
    if (!t) return;
    const norm = t.toUpperCase();
    let list = (this.recentSearches || []).filter((x) => x.toUpperCase() !== norm);
    list.unshift(t); // most recent first
    list = list.slice(0, 5);
    this.recentSearches = list;
    try {
      localStorage.setItem(this.RECENT_KEY, JSON.stringify(list));
    } catch {}
  }
  // recent-search pill → fill the box with that term and run the search again.
  goSearch(term: string): void {
    const t = (term || '').trim();
    if (!t) return;
    this.router.navigate(['/search-results', t]);
  }

  // ---- effective data (fixture in review mode, else live @Inputs) ----
  private get fx(): Fixture | null {
    return this.fixtureMode
      ? FIXTURES[(this.term || '').toUpperCase()] || null
      : null;
  }
  get effRegs(): Registration[] {
    return this.fx ? this.fx.rows : this.registrations || [];
  }
  private get effPerfect(): Registration[] {
    return this.fx ? this.fx.perfect : this.perfect || [];
  }

  // ---- counts ----
  get total(): number {
    return this.fx ? this.fx.close : this.effRegs.length;
  }
  get newCount(): number {
    return this.fx ? this.fx.newCount : this.newList.length;
  }
  // statistics row (§5) + results headline (§6).
  // Live: deduped union of every visible bucket (perfect + close/similar/new/
  // current all arrive merged in `registrations`) so the headline can NEVER read
  // 0 while perfect/close results are shown below.
  get foundCount(): number {
    if (this.fx) return this.fx.found;
    const seen = new Set<string>();
    const add = (list: Registration[]) =>
      (list || []).forEach((r) => {
        const k = (r?.registration || '').replace(/\s+/g, '').toUpperCase();
        if (k) seen.add(k);
      });
    add(this.registrations);
    add(this.perfect);
    return seen.size;
  }
  get closeCount(): number {
    return this.fx ? this.fx.close : this.effRegs.length;
  }
  get perfectCount(): number {
    return this.perfectCards.length;
  }
  // Sections are stacked now, so every perfect card is shown at the top of the
  // page (no per-tab filtering).
  get visiblePerfectCards(): PerfectCard[] {
    return this.perfectCards;
  }

  // ---- processed (filter + sort + auction-pinned) lists per stacked section ----
  // These are the EXPENSIVE derivations (filter + sort + auction-pin over up to
  // thousands of rows). They are computed once per data/filter/sort change in
  // recompute() and cached, NOT recomputed on every change-detection tick — the
  // getters below just hand back the cached arrays.
  private _closeRows: Registration[] = [];
  private _similarRows: Registration[] = [];
  private _newRows: Registration[] = [];
  private _currentViews: GroupView[] = [];
  private _prefixViews: GroupView[] = [];
  private _currentCount = 0;
  private _prefixCount = 0;
  private _tabs: TabDef[] = [];

  // Rebuild every cached section view. Cheap to call on real changes (data in,
  // filter/sort toggled, auctions arrive); never call it from a getter.
  private recompute(): void {
    this._closeRows = this.pinAuctions(this.applyView(this.closeList));
    this._similarRows = this.pinAuctions(this.applyView(this.similarList));
    this._newRows = this.pinAuctions(this.applyView(this.newListRaw));
    this._currentViews = this.fx
      ? this.buildGroupViews(this.fxGroups('cur'), this.currentList, 'cur')
      : this.buildGroupViews(this.currentGroups, this.currentList, 'cur');
    this._prefixViews = this.fx
      ? this.buildGroupViews(this.fxGroups('pre'), this.prefixList, 'pre')
      : this.buildGroupViews(this.prefixGroups, this.prefixList, 'pre');
    // Section/tab totals use the backend's true counts (sum of group counts).
    this._currentCount = this._currentViews.reduce((n, g) => n + g.count, 0);
    this._prefixCount = this._prefixViews.reduce((n, g) => n + g.count, 0);
    this._tabs = this.computeTabs();
  }

  get closeRows(): Registration[] {
    return this._closeRows;
  }
  get similarRows(): Registration[] {
    return this._similarRows;
  }
  get newRows(): Registration[] {
    return this._newRows;
  }
  /** Lowest available price across results, in pence (0 = none). */
  get pricedFrom(): number {
    if (this.fx) return this.fx.pricedFrom * 100;
    const prices = (this.effRegs || []).map((r) => r.price).filter((p) => p > 0);
    return prices.length ? Math.min(...prices) : 0;
  }
  get termUpper(): string {
    return (this.term || '').toUpperCase();
  }

  // ---- tab datasets ----
  // Raw (pre-filter) lists; `rows` applies the active view (type filter + sort).
  get closeList(): Registration[] {
    return this.effRegs;
  }
  get similarList(): Registration[] {
    if (this.fx) return this.fx.rows.slice(-3); // visual-review stand-in
    return this.similar || [];
  }
  // Flattened Current/Prefix lists — used for tab counts and auction scanning.
  // Prefer the grouped data (so counts match the stacked blocks); fall back to
  // the flat inputs, then to style-filtered close matches.
  get currentList(): Registration[] {
    if (this.fx) return this.fx.rows.filter((r) => (r.style || '').toLowerCase() === 'current');
    if (this.currentGroups && this.currentGroups.length) return this.flattenGroups(this.currentGroups);
    return (this.current && this.current.length
      ? this.current
      : (this.registrations || []).filter((r) => (r.style || '').toLowerCase() === 'current'));
  }
  get prefixList(): Registration[] {
    if (this.fx) return this.fx.rows.filter((r) => (r.style || '').toLowerCase() === 'prefix');
    if (this.prefixGroups && this.prefixGroups.length) return this.flattenGroups(this.prefixGroups);
    return (this.prefix && this.prefix.length
      ? this.prefix
      : (this.registrations || []).filter((r) => (r.style || '').toLowerCase() === 'prefix'));
  }

  // Visual-review only (fixture mode): synthesise a few labelled wildcard groups
  // from the fixture rows so the plate-style pattern labels (e.g. JA??ES?) render.
  private fxGroups(kind: 'cur' | 'pre'): PatternGroup[] {
    const rows = kind === 'cur' ? this.currentList : this.prefixList;
    if (!rows.length) return [];
    const defs = kind === 'cur'
      ? [{ label: 'JA??ES?', count: 230 }, { label: '????JES', count: 22841 }, { label: '?J??MES', count: 945 }]
      : [{ label: 'J??MES', count: 118 }, { label: '??JAM', count: 64 }];
    const per = Math.max(1, Math.ceil(rows.length / defs.length));
    return defs.map((d, i) => ({ label: d.label, regs: rows.slice(i * per, (i + 1) * per), count: d.count }));
  }

  private flattenGroups(groups: PatternGroup[]): Registration[] {
    return (groups || []).reduce(
      (acc, g) => acc.concat((g && g.regs) || []),
      [] as Registration[]
    );
  }

  // Non-empty pattern groups, each turned into a paginated GroupView. When no
  // real groups are supplied we synthesise a single group from the flat input
  // (or style-filtered close matches) so the section still renders + paginates.
  private buildGroupViews(
    groups: PatternGroup[],
    flatFallback: Registration[],
    prefix: 'cur' | 'pre'
  ): GroupView[] {
    const src: PatternGroup[] =
      groups && groups.length
        ? groups.filter((g) => g && g.regs && g.regs.length)
        : (flatFallback && flatFallback.length
            ? [{ label: '', regs: flatFallback }]
            : []);
    return src
      .map((g, i) => {
        const key = `${prefix}:${g.label || i}`;
        // applyView now strips registry-only (no-price) plates, so `rows` is the
        // buyable set and its length is the true available count for the group
        // (available-first ordering means every available plate is in this page).
        const rows = this.pinAuctions(this.applyView(g.regs));
        return { label: g.label, key, rows, loaded: rows.length, count: rows.length };
      })
      .filter((v) => v.rows.length > 0); // drop groups that were registry-only
  }
  get currentGroupViews(): GroupView[] {
    return this._currentViews;
  }
  get prefixGroupViews(): GroupView[] {
    return this._prefixViews;
  }
  // Cached section totals (sum of group totals) — used for the section count
  // badges without re-flattening the groups on every change-detection tick.
  get currentCount(): number {
    return this._currentCount;
  }
  get prefixCount(): number {
    return this._prefixCount;
  }
  get newListRaw(): Registration[] {
    if (this.fx) return this.fx.rows.slice(0, this.fx.newCount);
    const cut = Date.now() - 1000 * 60 * 60 * 24 * 30; // 30 days
    return (this.registrations || []).filter((r) => {
      const d = (r as any).date_added;
      if (!d) return false;
      const t = new Date(d).getTime();
      return !isNaN(t) && t >= cut;
    });
  }
  // kept for the headline count
  get newList(): Registration[] {
    return this.newListRaw;
  }

  // Tabs: Close + Market Performance always; the prod-style middle tabs
  // (Similar / New to market / Current / Prefix) appear only when populated.
  // Cached (see recompute) so the scroll-spy can read it cheaply every scroll.
  get visibleTabs(): TabDef[] {
    return this._tabs;
  }
  private computeTabs(): TabDef[] {
    const tabs: TabDef[] = [
      { key: 'close', label: 'Close Matches', count: this.closeCount },
    ];
    if (this._similarRows.length) tabs.push({ key: 'similar', label: 'Similar', count: this._similarRows.length });
    if (this._newRows.length) tabs.push({ key: 'new', label: 'New To Market', count: this.newCount });
    if (this._currentCount) tabs.push({ key: 'current', label: 'Current', count: this._currentCount });
    if (this._prefixCount) tabs.push({ key: 'prefix', label: 'Prefix', count: this._prefixCount });
    tabs.push({ key: 'market', label: 'Market Performance', count: null });
    return tabs;
  }
  get isListTab(): boolean {
    return this.activeTab !== 'market';
  }
  get rows(): Registration[] {
    let src: Registration[];
    switch (this.activeTab) {
      case 'similar': src = this.similarList; break;
      case 'new': src = this.newListRaw; break;
      case 'current': src = this.currentList; break;
      case 'prefix': src = this.prefixList; break;
      default: src = this.closeList;
    }
    return this.pinAuctions(this.applyView(src));
  }

  // Count of currently-listed (available) registrations in the active list.
  get availableCount(): number {
    return (this.effRegs || []).filter((r) => r && r.price > 0).length;
  }

  // Single summary line describing MARKET STATE, not match counts:
  // "{N} registrations · {A} available from £X" (or "· none currently listed").
  get summaryHtml(): string {
    const n = this.foundCount.toLocaleString('en-GB');
    const a = this.availableCount;
    const auc = this.auctionCount;
    const auction = auc > 0 ? ` &middot; <span class="sb-sum-auction">${auc.toLocaleString('en-GB')} at auction</span>` : '';
    if (a > 0) {
      const from = this.gbp(this.pricedFrom);
      return `<b>${n} registrations</b> &middot; ${a.toLocaleString('en-GB')} available from ${from}${auction}`;
    }
    return `<b>${n} registrations</b> &middot; none currently listed${auction}`;
  }

  private applyView(list: Registration[]): Registration[] {
    // The browsable list sections show only plates you can actually buy now
    // (a dealer/DVLA price, or a live/upcoming auction). Registry-only plates
    // with no price are excluded here — the exact plate is still offered with
    // "Notify" in the Perfect section, which does not go through this filter.
    let out = list.filter((r) => r && (r.price > 0 || !!this.auctionOf(r)));
    if (this.typeFilter) {
      out = out.filter((r) => (r.style || '').toLowerCase() === this.typeFilter);
    }
    if (this.saleType) {
      out = out.filter((r) => this.saleOf(r) === this.saleType);
    }
    if (this.priceBand) {
      out = out.filter((r) => this.bandOf(r.price) === this.priceBand);
    }
    // 'relevant' keeps the backend score order but floats available (priced)
    // plates above unavailable ones — matching prod and the registry note
    // ("Available plates are shown first"). Price sorts are opt-in and override.
    if (this.sortMode === 'low') out.sort((a, b) => a.price - b.price);
    else if (this.sortMode === 'high') out.sort((a, b) => b.price - a.price);
    else {
      out = out
        .map((r, i) => ({ r, i }))
        .sort((a, b) => {
          const av = a.r.price > 0 ? 0 : 1;
          const bv = b.r.price > 0 ? 0 : 1;
          return av !== bv ? av - bv : a.i - b.i; // stable within each group
        })
        .map((x) => x.r);
    }
    return out;
  }

  // New issue = never registered to a vehicle; otherwise a resale.
  private saleOf(r: Registration): 'new' | 'resale' {
    return r && r.registered ? 'resale' : 'new';
  }
  // Price band buckets (price stored in pence; 0/absent = not priced, excluded).
  private bandOf(pence: number): '' | 'u500' | 'mid' | 'high' | 'top' {
    if (!(pence > 0)) return '';
    const p = pence / 100;
    if (p < 500) return 'u500';
    if (p < 1000) return 'mid';
    if (p < 5000) return 'high';
    return 'top';
  }

  // Tab buttons are jump links: clicking scrolls the matching stacked section
  // to just below the sticky header + tab bar. `activeTab` is kept in sync by
  // the scroll-spy so the right tab stays highlighted as the user scrolls.
  private sectionId(key: TabKey): string {
    return 'sb-sec-' + key;
  }
  // Combined height of everything pinned to the top (the PlateX header, when it
  // is sticky on the search page, + this tab bar), measured live so it is right
  // at any viewport. Section headings scroll to just below this.
  private stickyOffset(): number {
    if (typeof document === 'undefined') return 120;
    let h = 0;
    const nav = document.querySelector('pla-nav-shell') as HTMLElement | null;
    if (nav && getComputedStyle(nav).position === 'sticky') {
      h += nav.getBoundingClientRect().height;
    }
    const tabs = document.querySelector('.sb-tabs--sticky') as HTMLElement | null;
    if (tabs) h += tabs.getBoundingClientRect().height;
    return Math.round(h + 6);
  }
  // Whether the stacked result sections are shown. Market Performance is a real
  // content-swap tab, not a stacked section — selecting it hides the results and
  // shows the market panel instead.
  get resultsView(): boolean {
    return this.activeTab !== 'market';
  }
  setTab(t: TabKey): void {
    const wasMarket = this.activeTab === 'market';
    this.activeTab = t;
    // Market Performance swaps content in place — no scroll.
    if (t === 'market' || typeof document === 'undefined') return;
    const doScroll = () => {
      const el = document.getElementById(this.sectionId(t));
      if (!el) return;
      const y = el.getBoundingClientRect().top + window.scrollY - this.stickyOffset();
      window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
    };
    // Coming back from the market panel, the result sections re-enter the DOM
    // this tick — wait for them before scrolling.
    if (wasMarket) setTimeout(doScroll, 0);
    else doScroll();
  }

  // Scroll-spy: highlight the tab whose section is nearest the top of the
  // viewport (just under the sticky bar). Cheap — only runs over visible tabs.
  @HostListener('window:scroll')
  onWindowScroll(): void {
    if (typeof document === 'undefined') return;
    // In the Market Performance view the result sections aren't rendered, so the
    // scroll-spy has nothing to track — leave that tab active.
    if (this.activeTab === 'market') return;
    const probe = this.stickyOffset() + 8;
    let best: TabKey | null = null;
    let bestTop = -Infinity;
    for (const t of this.visibleTabs) {
      const el = document.getElementById(this.sectionId(t.key));
      if (!el) continue;
      const top = el.getBoundingClientRect().top - probe;
      // the last section whose top has passed the probe line wins
      if (top <= 0 && top > bestTop) {
        bestTop = top;
        best = t.key;
      }
    }
    if (best && best !== this.activeTab) this.activeTab = best;
  }
  setType(t: SearchBodyV2Component['typeFilter']): void {
    this.typeFilter = this.typeFilter === t ? '' : t;
    this.onViewChanged();
  }
  setSale(t: SearchBodyV2Component['saleType']): void {
    this.saleType = this.saleType === t ? '' : t;
    this.onViewChanged();
  }
  setBand(t: SearchBodyV2Component['priceBand']): void {
    this.priceBand = this.priceBand === t ? '' : t;
    this.onViewChanged();
  }
  // A filter / sort change re-derives the cached views and collapses every
  // section back to its first page so the user sees the top of the new view.
  private onViewChanged(): void {
    this.resetPagination();
    this.recompute();
  }

  // ---- Filter dropdown (replaces the old always-visible chip row) ----
  toggleFilter(ev: Event): void {
    ev.stopPropagation();
    this.filterOpen = !this.filterOpen;
    if (this.filterOpen) this.sortOpen = false;
  }
  @ViewChild('filterWrap') filterWrap?: ElementRef<HTMLElement>;

  // ---- Sort dropdown (Most Relevant default, then price low/high) ----
  toggleSort(ev: Event): void {
    ev.stopPropagation();
    this.sortOpen = !this.sortOpen;
    if (this.sortOpen) this.filterOpen = false;
  }
  setSort(mode: SearchBodyV2Component['sortMode']): void {
    this.sortMode = mode;
    this.sortOpen = false;
    this.onViewChanged();
  }
  get sortLabel(): string {
    return this.sortMode === 'low'
      ? 'Price Low to High'
      : this.sortMode === 'high'
      ? 'Price High to Low'
      : 'Most Relevant';
  }
  @ViewChild('sortWrap') sortWrap?: ElementRef<HTMLElement>;

  @HostListener('document:click', ['$event'])
  onDocClick(ev: MouseEvent): void {
    const t = ev.target as Node;
    if (this.filterOpen) {
      const el = this.filterWrap?.nativeElement;
      if (el && !el.contains(t)) this.filterOpen = false;
    }
    if (this.sortOpen) {
      const el = this.sortWrap?.nativeElement;
      if (el && !el.contains(t)) this.sortOpen = false;
    }
  }

  get hasActiveFilters(): boolean {
    return !!(this.typeFilter || this.saleType || this.priceBand);
  }
  // Active-filter pills shown beneath the summary line (only when set).
  get activeFilters(): { kind: 'type' | 'sale' | 'price'; label: string }[] {
    const out: { kind: 'type' | 'sale' | 'price'; label: string }[] = [];
    if (this.typeFilter) {
      const cap = this.typeFilter.charAt(0).toUpperCase() + this.typeFilter.slice(1);
      out.push({ kind: 'type', label: cap });
    }
    if (this.saleType) {
      out.push({ kind: 'sale', label: this.saleType === 'new' ? 'New issue' : 'Resale' });
    }
    if (this.priceBand) {
      const labels: { [k: string]: string } = {
        u500: 'Under £500',
        mid: '£500–£1,000',
        high: '£1,000–£5,000',
        top: '£5,000+',
      };
      out.push({ kind: 'price', label: labels[this.priceBand] });
    }
    return out;
  }
  clearFilter(kind: 'type' | 'sale' | 'price'): void {
    if (kind === 'type') this.typeFilter = '';
    else if (kind === 'sale') this.saleType = '';
    else this.priceBand = '';
    this.onViewChanged();
  }

  // ---- presentation helpers ----
  // arrangement word for the perfect-card subtitle ("Prefix Perfect “JAMES” Plate").
  // trailing space so an unknown style collapses to plain "Perfect “JAMES” Plate".
  perfectClass(r: Registration): string {
    const map: { [k: string]: string } = {
      prefix: 'Prefix ',
      suffix: 'Suffix ',
      dateless: 'Dateless ',
      current: 'Current ',
    };
    return map[(r.style || '').toLowerCase()] || '';
  }
  // Market depth, NOT seller identity. Only when more than one seller lists the
  // plate — a discovery signpost; the actual dealer comparison lives on Plate
  // Detail. Single-seller or unknown → no label (availability alone is enough).
  compareLabel(r: Registration): string {
    const n = (r as any).sellers;
    if (!n || n < 2) return '';
    return `Compare ${n} sellers`;
  }
  // Split a wildcard pattern (e.g. "HU??GH?") so each '?' can be set in a
  // plain font: UKReg has no '?' glyph of its own.
  patChars(label: string): string[] {
    return (label || '').split('');
  }
  gbp(pence: number): string {
    return '£' + Math.round(pence / 100).toLocaleString('en-GB');
  }

  // ---- actions ----
  view(r: Registration): void {
    const reg = (r.registration || '').replace(/\s+/g, '');
    if (!reg) return;
    this.router.navigate([platePath(reg)]);
  }

  // Perfect Plate click — carries the pinned presentation asset (id + source)
  // into Plate Detail as query params so the hero image is the SAME car shown
  // on the card. Plate Detail reuses this rather than picking its own hero.
  viewPerfect(card: PerfectCard): void {
    const reg = (card.reg.registration || '').replace(/\s+/g, '');
    if (!reg) return;
    this.router.navigate([platePath(reg)], {
      queryParams: { pa: card.assetId, pas: card.source },
    });
  }
  // recent-search pill → open that registration's detail page.
  viewReg(reg: string): void {
    const r = (reg || '').replace(/\s+/g, '');
    if (!r) return;
    this.router.navigate([platePath(r)]);
  }
  // collection card → its CATEGORY page (dateless-search / number-1 / new-release).
  goCollection(link: string): void {
    if (!link) return;
    this.router.navigateByUrl(link);
  }

  get titleTerm(): string {
    const t = (this.term || '').trim();
    if (!t) return '';
    return t.charAt(0).toUpperCase() + t.slice(1).toLowerCase();
  }

  // sentence-case heading uses the raw term in lower case ("... found for james")
  get termLower(): string {
    return (this.term || '').trim().toLowerCase();
  }

  // ---- Perfect Match cards (max 3, curated premium imagery) ----
  private buildPerfect(): void {
    // AVAILABLE perfect matches lead; unavailable ones still appear (the Registry
    // holds every registration) but fall to the end and render secondary. Stable
    // order within each group preserves the backend's relevance ranking.
    const ranked = (this.effPerfect || [])
      .filter((r) => r && r.registration)
      .slice()
      .sort((a, b) => (b.price > 0 ? 1 : 0) - (a.price > 0 ? 1 : 0));
    const src = ranked.slice(0, 3);
    // The presentation asset is now DETERMINISTIC in the plate identity (not a
    // term seed) so it can be reproduced on Plate Detail. Each card carries its
    // stable assetId + source into the plate view for visual continuity.
    this.perfectCards = src.map((reg, i) => {
      const asset = resolvePresentationAsset(reg.registration);
      return {
        reg,
        label: this.perfectLabels[i] || this.perfectLabels[0],
        img: null,
        cfg: asset.config,
        carName: asset.carName,
        assetId: asset.id,
        source: asset.source,
      };
    });
    if (!this.perfectCards.length) return;
    this.renderPerfectImage(0); // sequential renders, distinct premium vehicle each
  }

  private renderPerfectImage(i: number): void {
    if (i >= this.perfectCards.length) return;
    const card = this.perfectCards[i];
    const next = () => this.renderPerfectImage(i + 1);
    if (!card.cfg) {
      next();
      return;
    }
    try {
      this.imageService.INIT();
      const formatted = card.reg.formattedRegistration || card.reg.registration;
      this.imageService.quickBuildWithCrops(card.cfg, formatted, (r) => {
        // full car image preferred — premium catalogue look, not a zoomed bumper crop
        card.img = (r && (r.full || r.detailMain)) || null;
        card.carName = card.cfg ? card.cfg.carName : null;
        next();
      });
    } catch {
      next();
    }
  }
}

/** Display model for a plate's live / coming-soon auction (search rows + cards). */
export interface AuctionInfo {
  kind: 'live' | 'soon';
  lot: AuctionLotDto;
  closesAt: number;
  opensAt: number;
  /** Row status label ("At auction" / "Coming to auction"). */
  status: string;
  /** Price-column lead ("LIVE AT" / "STARTING" / "GUIDE FROM" / "NO BIDS YET" / "OPENS"). */
  lead: string;
  /** Price-column value ("£6,500" / "Wed 18 Nov"); '' when the lead says it all. */
  value: string;
  /** Perfect-card corner pill ("At auction · ends Wed 10:00"). */
  flag: string;
}

const LONDON = 'Europe/London';
function aucGbp(pence: number): string {
  return '£' + Math.round(pence / 100).toLocaleString('en-GB');
}
function aucDay(t: number): string {
  return new Date(t).toLocaleDateString('en-GB', { timeZone: LONDON, weekday: 'short', day: 'numeric', month: 'short' });
}
function aucWeekdayTime(t: number): string {
  const d = new Date(t);
  const wd = d.toLocaleDateString('en-GB', { timeZone: LONDON, weekday: 'short' });
  const hm = d.toLocaleTimeString('en-GB', { timeZone: LONDON, hour: '2-digit', minute: '2-digit' });
  return wd + ' ' + hm;
}

/** Live/soon lot -> display model; null for ended lots. Never "£0" or blank. */
export function toAuctionInfo(lot: AuctionLotDto, now: number): AuctionInfo | null {
  const closesAt = lot.biddingClosesAt ? Date.parse(lot.biddingClosesAt) : 0;
  const opensAt = lot.biddingOpensAt ? Date.parse(lot.biddingOpensAt) : 0;
  if (lot.status === 'ended' || (closesAt && now >= closesAt)) return null;
  if (lot.status === 'soon' && (!opensAt || now < opensAt)) {
    const day = opensAt ? aucDay(opensAt) : '';
    return {
      kind: 'soon',
      lot,
      closesAt,
      opensAt,
      status: 'Coming to auction',
      lead: day ? 'OPENS' : 'OPENS SOON',
      value: day,
      flag: 'Coming to auction' + (day ? ' · opens ' + day : ''),
    };
  }
  let lead = 'NO BIDS YET';
  let value = '';
  if (lot.currentBidPence != null && lot.currentBidPence > 0) {
    lead = 'LIVE AT';
    value = aucGbp(lot.currentBidPence);
  } else if (lot.startingPricePence != null && lot.startingPricePence > 0) {
    lead = 'STARTING';
    value = aucGbp(lot.startingPricePence);
  } else if (lot.guideLowPence != null && lot.guideLowPence > 0) {
    lead = 'GUIDE FROM';
    value = aucGbp(lot.guideLowPence);
  }
  return {
    kind: 'live',
    lot,
    closesAt,
    opensAt,
    status: 'At auction',
    lead,
    value,
    flag: 'At auction' + (closesAt ? ' · ends ' + aucWeekdayTime(closesAt) : ''),
  };
}
